import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { format } from 'date-fns';
import {
  CalendarIcon, ShoppingCart, ChefHat, Wine, Package, FileText, Save,
  ChevronDown, ChevronUp, AlertTriangle, Star, Lock, CheckCircle2, Pencil, Clock, BedDouble, ShieldAlert, Upload
} from 'lucide-react';
import { fmtTimeIST } from '../lib/tz';
import PosImportModal from '../components/daily_entry/PosImportModal';

const emptyCategory = { opening: '', issues: '', closing: '' };

const initialForm = {
  budget: '', gross_sales: '',
  food_sale: '', beverage_sale: '', other_sale: '',
  number_of_covers: '',
  // Service-period split (optional but validated when filled)
  lunch_sales: '', lunch_covers: '', dinner_sales: '', dinner_covers: '',
  channel_dine_in: '', channel_swiggy: '', channel_zomato: '', channel_other: '',
  // Channel order counts — required whenever the matching channel sales > 0
  channel_dine_in_orders: '', channel_swiggy_orders: '', channel_zomato_orders: '', channel_other_orders: '',
  kitchen_total: { ...emptyCategory },
  bar_total: { ...emptyCategory },
  kitchen_issues: '', bar_issues: '', hk_issues: '', other_purchases: '',
  remarks: '',
  direct_reviews: '', avg_rating_day: '', nps_day: '',
  last_year_sales: '',
  rooms_available: '', rooms_sold: '', room_revenue: '',
  room_revenue_budget: '', room_revenue_last_year: ''
};

const SECTION_LABELS = {
  sales_data: 'Sales Data',
  food_cost: 'Food Cost',
  beverage_cost: 'Beverage Cost',
  other_issues: 'Store Issues / Transfers',
  customer_experience: 'Customer Experience',
  notes: 'Notes'
};

const SECTION_FIELDS = {
  sales_data: ['budget', 'gross_sales', 'food_sale', 'beverage_sale', 'other_sale', 'number_of_covers',
    'channel_dine_in', 'channel_swiggy', 'channel_zomato', 'channel_other',
    'channel_dine_in_orders', 'channel_swiggy_orders', 'channel_zomato_orders', 'channel_other_orders',
    'lunch_sales', 'lunch_covers', 'dinner_sales', 'dinner_covers', 'last_year_sales',
    'rooms_available', 'rooms_sold', 'room_revenue', 'room_revenue_budget'],
  // ITER246 — room_revenue_last_year is INTENTIONALLY excluded. It is frozen,
  // display-only, auto-derived from history; the form never submits it back.
  food_cost: ['kitchen_total'],
  beverage_cost: ['bar_total'],
  other_issues: ['kitchen_issues', 'bar_issues', 'hk_issues', 'other_purchases'],
  customer_experience: ['direct_reviews', 'avg_rating_day', 'nps_day'],
  notes: ['remarks']
};

const num = (v) => parseFloat(v) || 0;
const fmt = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;

function SectionHeader({ icon: Icon, title, open, onToggle, badge, sectionStatus, pinCorrected }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 text-left hover:bg-[#F3F5F2]/50 transition-colors rounded-t-lg"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Icon className="w-5 h-5 text-[color:var(--color-primary)] flex-shrink-0" strokeWidth={1.5} />
        <h3 className="font-semibold text-[#1A2421] truncate">{title}</h3>
        {badge && <Badge variant="outline" className="text-xs border-[#E2E8DF] text-[#5C6B64] flex-shrink-0">{badge}</Badge>}
        {pinCorrected && (
          <Badge
            className="text-[10px] bg-[#D4A017]/10 text-[#B88718] border-[#D4A017]/30 font-semibold uppercase tracking-wide flex-shrink-0"
            data-testid="pin-corrected-tag"
            title="This section was unlocked and edited via PIN verification."
          >
            Corrected via PIN
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {sectionStatus && (
          sectionStatus.locked ? (
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#2D6A4F]" />
              <span className="text-[10px] text-[#2D6A4F] font-medium hidden sm:inline">Submitted</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-[#D4A017]" />
              <span className="text-[10px] text-[#D4A017] font-medium hidden sm:inline">Pending</span>
            </div>
          )
        )}
        {open ? <ChevronUp className="w-4 h-4 text-[#5C6B64]" /> : <ChevronDown className="w-4 h-4 text-[#5C6B64]" />}
      </div>
    </button>
  );
}

function NumberInput({ label, value, onChange, required, testId, placeholder, disabled, size = 'default' }) {
  const big = size === 'big';
  // ITER249 — Display rule: NO decimals on screen. When disabled (locked),
  // we render a rounded display value while keeping the form state at full
  // 2dp precision. When editable, the raw (precise) value is shown so the
  // user can type/edit without confusion. Storage and reconciliation gates
  // always run on the raw value — we never mutate `value` itself here.
  const displayValue = (() => {
    if (!disabled) return value;
    if (value === '' || value === null || value === undefined) return value;
    const n = Number(value);
    if (Number.isNaN(n)) return value;
    return String(Math.round(n));
  })();
  return (
    <div className="space-y-1.5">
      <Label className={`${big ? 'text-sm' : 'text-[13px]'} font-medium text-[#1A2421]`}>
        {label}{required && <span className="text-[#C45A41]"> *</span>}
      </Label>
      <Input
        data-testid={testId}
        type="number"
        inputMode="decimal"
        step="any"
        placeholder={placeholder || '0'}
        value={displayValue}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className={`${big ? 'h-12 text-lg font-bold tabular-nums' : 'h-10 text-sm'} bg-white border border-[#1A2421]/15 hover:border-[#1A2421]/30 focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] placeholder:text-[#5C6B64]/60 ${disabled ? 'bg-[#F3F5F2] cursor-not-allowed opacity-70' : ''}`}
      />
    </div>
  );
}

// ITER250 — Sales Data read-view. Presentation-only summary rendered after
// the section is imported/locked. Values come from the exact same form/state
// the edit-view writes to; no recomputation, no new fetches, no behavior
// changes. Switch is driven by `salesLocked` (the existing lock signal).
function SalesComparisonRow({ label, refValue, netSales, periodNote, testId }) {
  const hasRef = refValue != null && refValue > 0;
  const g = (hasRef && netSales > 0)
    ? { pct: Math.round((netSales - refValue) / refValue * 100), up: netSales >= refValue }
    : null;
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#F3F5F2] last:border-0" data-testid={testId}>
      <div className="flex flex-col min-w-0">
        <span className="text-[12px] text-[#5C6B64]">{label}</span>
        {periodNote && (
          <span className="text-[10px] text-[#5C6B64]/80 mt-0.5">{periodNote}</span>
        )}
      </div>
      <div className="flex items-baseline gap-3 shrink-0">
        <span className="text-sm font-medium tabular-nums text-[#1A2421]">
          {hasRef ? fmt(refValue) : '—'}
        </span>
        {g ? (
          <span
            className={`text-[12px] font-semibold tabular-nums inline-flex items-center gap-0.5 ${g.up ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
            data-testid={testId ? `${testId}-growth` : undefined}>
            <span aria-hidden>{g.up ? '↗' : '↘'}</span>
            {g.up ? '+' : ''}{g.pct}%
          </span>
        ) : (
          <span className="text-[12px] text-[#5C6B64]/60 w-12 text-right">—</span>
        )}
      </div>
    </div>
  );
}

function SalesChannelRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#F3F5F2] last:border-0" data-testid={testId}>
      <span className="text-[12px] text-[#5C6B64]">{label}</span>
      <span className={`text-sm tabular-nums ${value > 0 ? 'font-medium text-[#1A2421]' : 'text-[#5C6B64]/60'}`}>
        {value > 0 ? fmt(value) : '—'}
      </span>
    </div>
  );
}

function SalesReadView({
  form, prefill, lastYearDateStr,
  netSales, channelMatched, salesSplitMatched, hasSalesMismatch,
  covers, foodApc, bevApc, totalApc,
  roomsEnabled, roomsSold, roomsAvail, roomRev, occupancyPct, roomKpis,
}) {
  const budget = num(form.budget);
  const lyDate = num(form.last_year_sales);
  const lySameDay = prefill.last_year_same_day_sales != null
    ? Number(prefill.last_year_same_day_sales) : null;

  const food = num(form.food_sale);
  const bev = num(form.beverage_sale);
  const other = num(form.other_sale);
  const lunchS = num(form.lunch_sales);
  const lunchC = parseInt(form.lunch_covers) || 0;
  const dinnerS = num(form.dinner_sales);
  const dinnerC = parseInt(form.dinner_covers) || 0;
  const chDine = num(form.channel_dine_in);
  const chSwiggy = num(form.channel_swiggy);
  const chZomato = num(form.channel_zomato);
  const chOther = num(form.channel_other);

  // The footer is driven by the existing reconciliation gate state — when
  // the section was submitted/imported (and we're in read-view) hasSales-
  // Mismatch will be false. We keep the calm "all matched" cue here.
  const ready = !hasSalesMismatch && (netSales === 0 || (channelMatched && salesSplitMatched));

  return (
    <div className="section-enter" data-testid="sales-readview">
      {/* Hero — Net Sales with nested comparisons */}
      <div className="pt-1 pb-1" data-testid="sales-readview-hero">
        <p className="text-[10px] uppercase tracking-[0.08em] text-[#5C6B64] font-semibold mb-1">
          Net Sales
        </p>
        <p className="text-4xl sm:text-5xl font-bold tabular-nums text-[color:var(--color-primary)] leading-none"
           data-testid="sales-readview-net-sales">
          {fmt(netSales)}
        </p>
        <div className="mt-4 ml-1 pl-4 border-l-2 border-[#E2E8DF]"
             data-testid="sales-readview-comparisons">
          <SalesComparisonRow label="Budget" refValue={budget} netSales={netSales}
                         testId="sales-readview-cmp-budget" />
          {prefill.last_year_same_day_date && (
            <SalesComparisonRow
              label="Last year · same day"
              refValue={lySameDay}
              netSales={netSales}
              periodNote={`${prefill.last_year_same_day_name || ''} · ${prefill.last_year_same_day_date}`.trim()}
              testId="sales-readview-cmp-ly-same-day" />
          )}
          <SalesComparisonRow
            label="Last year · same date"
            refValue={lyDate}
            netSales={netSales}
            periodNote={lastYearDateStr}
            testId="sales-readview-cmp-ly-same-date" />
        </div>
      </div>

      {/* Food / Beverage / Other */}
      <div className="mt-5 pt-4 border-t border-[#E2E8DF]">
        <div className="grid grid-cols-3 divide-x divide-[#F3F5F2]"
             data-testid="sales-readview-sales-split">
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Food</p>
            <p className="text-base font-semibold tabular-nums text-[#1A2421]"
               data-testid="sales-readview-food">{fmt(food)}</p>
          </div>
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">
              Beverage <span className="text-[#5C6B64]/70 normal-case">(incl. liquor)</span>
            </p>
            <p className="text-base font-semibold tabular-nums text-[#1A2421]"
               data-testid="sales-readview-beverage">{fmt(bev)}</p>
          </div>
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Other</p>
            <p className="text-base font-semibold tabular-nums text-[#1A2421]"
               data-testid="sales-readview-other">{fmt(other)}</p>
          </div>
        </div>
        {/* Covers + APC row */}
        {covers > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-[#F3F5F2] mt-1 pt-2 border-t border-[#F3F5F2]"
               data-testid="sales-readview-covers-row">
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Covers</p>
              <p className="text-sm font-medium tabular-nums text-[#1A2421]"
                 data-testid="sales-readview-covers">{covers.toLocaleString('en-IN')}</p>
            </div>
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Food APC</p>
              <p className="text-sm font-medium tabular-nums text-[#1A2421]">{fmt(foodApc)}</p>
            </div>
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Bev APC</p>
              <p className="text-sm font-medium tabular-nums text-[#1A2421]">{fmt(bevApc)}</p>
            </div>
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Total APC</p>
              <p className="text-sm font-medium tabular-nums text-[#1A2421]">{fmt(totalApc)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Service Split */}
      {(lunchS + dinnerS) > 0 && (
        <div className="mt-5 pt-4 border-t border-[#E2E8DF]"
             data-testid="sales-readview-service-split">
          <p className="text-[10px] uppercase tracking-[0.08em] text-[#5C6B64] font-semibold mb-2">
            Service Split
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#F3F5F2]">
            <div className="px-3 py-2 flex items-baseline justify-between"
                 data-testid="sales-readview-lunch">
              <span className="text-[12px] text-[#5C6B64]">Lunch</span>
              <span className="text-sm font-medium tabular-nums text-[#1A2421]">
                {fmt(lunchS)}
                <span className="text-[11px] text-[#5C6B64] ml-1.5">· {lunchC} covers</span>
              </span>
            </div>
            <div className="px-3 py-2 flex items-baseline justify-between"
                 data-testid="sales-readview-dinner">
              <span className="text-[12px] text-[#5C6B64]">Dinner</span>
              <span className="text-sm font-medium tabular-nums text-[#1A2421]">
                {fmt(dinnerS)}
                <span className="text-[11px] text-[#5C6B64] ml-1.5">· {dinnerC} covers</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Channel Split */}
      <div className="mt-5 pt-4 border-t border-[#E2E8DF]"
           data-testid="sales-readview-channel-split">
        <p className="text-[10px] uppercase tracking-[0.08em] text-[#5C6B64] font-semibold mb-2">
          Channel Split
        </p>
        <div>
          <SalesChannelRow label="Dine-in" value={chDine} testId="sales-readview-channel-dine-in" />
          <SalesChannelRow label="Swiggy"  value={chSwiggy} testId="sales-readview-channel-swiggy" />
          <SalesChannelRow label="Zomato"  value={chZomato} testId="sales-readview-channel-zomato" />
          <SalesChannelRow label="Other"   value={chOther}  testId="sales-readview-channel-other" />
        </div>
      </div>

      {/* Rooms */}
      {roomsEnabled && (
        <div className="mt-5 pt-4 border-t border-[#E2E8DF]"
             data-testid="sales-readview-rooms">
          <div className="flex items-center gap-2 mb-3">
            <BedDouble className="w-4 h-4 text-[color:var(--color-primary)]" />
            <p className="text-[10px] uppercase tracking-[0.08em] text-[#5C6B64] font-semibold">
              Rooms
            </p>
          </div>
          <div className="grid grid-cols-3 divide-x divide-[#F3F5F2]">
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Available</p>
              <p className="text-sm font-medium tabular-nums text-[#1A2421]">{roomsAvail}</p>
            </div>
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Sold</p>
              <p className="text-sm font-medium tabular-nums text-[#1A2421]">{roomsSold}</p>
            </div>
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Room Revenue</p>
              <p className="text-sm font-medium tabular-nums text-[#1A2421]">{fmt(roomRev)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-[#F3F5F2] mt-1 pt-2 border-t border-[#F3F5F2]">
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1">Budget</p>
              <p className="text-sm font-medium tabular-nums text-[#1A2421]">
                {fmt(num(form.room_revenue_budget))}
              </p>
            </div>
            <div className="px-3 py-2" data-testid="sales-readview-room-ly">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1 inline-flex items-center gap-1">
                Last Year <Lock className="w-2.5 h-2.5" />
              </p>
              <p className="text-sm font-medium tabular-nums text-[#5C6B64]">
                {num(form.room_revenue_last_year) > 0 ? fmt(form.room_revenue_last_year) : '—'}
              </p>
            </div>
          </div>
          {(roomsSold > 0 || roomsAvail > 0 || roomKpis.arr > 0 || roomKpis.revpar > 0 || roomKpis.room_pax > 0) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              {(roomsSold > 0 || roomsAvail > 0) && (
                <div className="p-2 bg-[color:var(--color-primary)]/[0.04] border border-[color:var(--color-primary)]/10 rounded-lg text-center">
                  <p className="text-[10px] text-[#5C6B64] uppercase">Occupancy %</p>
                  <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums">{occupancyPct}%</p>
                </div>
              )}
              {roomKpis.arr > 0 && (
                <div className="p-2 bg-[color:var(--color-primary)]/[0.04] border border-[color:var(--color-primary)]/10 rounded-lg text-center"
                     data-testid="sales-readview-kpi-arr">
                  <p className="text-[10px] text-[#5C6B64] uppercase">ARR</p>
                  <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums">{fmt(roomKpis.arr)}</p>
                </div>
              )}
              {roomKpis.revpar > 0 && (
                <div className="p-2 bg-[color:var(--color-primary)]/[0.04] border border-[color:var(--color-primary)]/10 rounded-lg text-center"
                     data-testid="sales-readview-kpi-revpar">
                  <p className="text-[10px] text-[#5C6B64] uppercase">RevPar</p>
                  <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums">{fmt(roomKpis.revpar)}</p>
                </div>
              )}
              {roomKpis.room_pax > 0 && (
                <div className="p-2 bg-[color:var(--color-primary)]/[0.04] border border-[color:var(--color-primary)]/10 rounded-lg text-center"
                     data-testid="sales-readview-kpi-room-pax">
                  <p className="text-[10px] text-[#5C6B64] uppercase">Room Pax</p>
                  <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums">{roomKpis.room_pax}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer status — driven by EXISTING reconciliation gate, no new logic */}
      <div className="mt-5 pt-3 border-t border-[#E2E8DF]" data-testid="sales-readview-status">
        {ready ? (
          <div className="inline-flex items-center gap-1.5 text-xs text-[#2D6A4F]">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Submitted · all splits matched</span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5 text-xs text-[#C45A41]">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Split mismatch — unlock to reconcile</span>
          </div>
        )}
      </div>
    </div>
  );
}



function SectionSubmitBar({ sectionKey, sectionSub, onSubmit, onEdit, submitting, disabled, editMode, onRequestOverride, overrideStatus, moduleWritable, isPastDate, pinSet, onPinEdit }) {
  const isLocked = sectionSub?.locked;
  const editCount = sectionSub?.edit_count || 0;
  const canEdit = isLocked ? editCount < 2 : true;
  const subTime = sectionSub?.submitted_at ? fmtTimeIST(sectionSub.submitted_at) : '';
  const isBlocked = isLocked && !canEdit;
  const showOverrideBtn = isBlocked && !overrideStatus;

  // Override approved on a past date — special PIN-based editing flow
  const overrideUnlockedPast = isPastDate && overrideStatus === 'approved';

  // View Only — supervisor without module access
  if (moduleWritable === false) {
    return (
      <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#F3F5F2] rounded-lg" data-testid={`view-only-${sectionKey}`}>
          <Lock className="w-3.5 h-3.5 text-[#5C6B64]" />
          <span className="text-xs text-[#5C6B64] font-medium">View Only Access</span>
          <span className="text-[10px] text-[#5C6B64] ml-auto">Module not assigned to you</span>
        </div>
      </div>
    );
  }

  // Override approved for past date — show PIN-based edit prompt
  if (overrideUnlockedPast) {
    return (
      <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {isLocked && <Lock className="w-3.5 h-3.5 text-[#2D6A4F]" />}
            {isLocked && <span className="text-xs text-[#2D6A4F] font-medium">Submitted</span>}
            {subTime && <span className="text-[10px] text-[#5C6B64]">at {subTime}</span>}
          </div>
          <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]" data-testid={`override-approved-${sectionKey}`}>
            Override Approved
          </Badge>
        </div>
        <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`override-pin-prompt-${sectionKey}`}>
          <ShieldAlert className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-medium text-emerald-700">Override approved — enter PIN to edit</p>
            {!pinSet && <p className="text-[10px] text-red-600 mt-0.5">PIN not set. Contact admin to set your PIN.</p>}
          </div>
          {pinSet && (
            <Button size="sm" onClick={() => onPinEdit(sectionKey)}
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`pin-edit-${sectionKey}`}>
              <Pencil className="w-3 h-3 mr-1" /> Edit with PIN
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!isLocked) {
    // Past date with approved override — show PIN-gated submit
    if (overrideUnlockedPast) {
      return (
        <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]" data-testid={`override-approved-${sectionKey}`}>
              Override Approved
            </Badge>
          </div>
          <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`override-pin-prompt-${sectionKey}`}>
            <ShieldAlert className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-medium text-emerald-700">Override approved — enter PIN to submit</p>
              {!pinSet && <p className="text-[10px] text-red-600 mt-0.5">PIN not set. Contact admin to set your PIN.</p>}
            </div>
            {pinSet && (
              <Button size="sm" onClick={() => onPinEdit(sectionKey)}
                disabled={submitting || disabled}
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`pin-submit-${sectionKey}`}>
                <Save className="w-3 h-3 mr-1" /> Submit with PIN
              </Button>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
        <Button onClick={() => onSubmit(sectionKey)} disabled={submitting || disabled}
          className="w-full h-10 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-sm"
          data-testid={`submit-section-${sectionKey}`}>
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {submitting ? 'Submitting...' : `Submit ${SECTION_LABELS[sectionKey]}`}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-[#2D6A4F]" />
          <span className="text-xs text-[#2D6A4F] font-medium">Submitted</span>
          {subTime && <span className="text-[10px] text-[#5C6B64]">at {subTime}</span>}
        </div>
        <div className="flex items-center gap-2">
          {editCount > 0 && (
            <span className="text-[10px] text-[#D4A017]">Edited {editCount}/2</span>
          )}
          {canEdit && editMode && (
            <Button variant="outline" size="sm" onClick={() => onEdit(sectionKey)}
              className="h-7 text-xs border-[#E2E8DF] text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)]"
              data-testid={`edit-section-${sectionKey}`}>
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
          )}
          {canEdit && !editMode && (
            <span className="text-[10px] text-[#5C6B64]">Edit from History</span>
          )}
        </div>
      </div>
      {/* Override controls — visible when blocked for ANY reason */}
      {(showOverrideBtn || overrideStatus) && (
        <div className="flex items-center gap-2 mt-1.5">
          {showOverrideBtn && (
            <Button variant="outline" size="sm" onClick={() => onRequestOverride(sectionKey)}
              className="h-7 text-xs border-amber-300 text-amber-600 hover:bg-amber-50"
              data-testid={`request-override-${sectionKey}`}>
              <ShieldAlert className="w-3 h-3 mr-1" /> Request Override
            </Button>
          )}
          {overrideStatus === 'pending' && (
            <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[9px]" data-testid={`override-pending-${sectionKey}`}>
              <Clock className="w-2.5 h-2.5 mr-0.5" /> Override Requested
            </Badge>
          )}
          {overrideStatus === 'approved' && (
            <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]" data-testid={`override-approved-${sectionKey}`}>
              Override Approved
            </Badge>
          )}
          {overrideStatus === 'rejected' && (
            <Badge className="bg-red-50 text-red-600 border-red-200 text-[9px]" data-testid={`override-rejected-${sectionKey}`}>
              Override Rejected
            </Badge>
          )}
        </div>
      )}
      {sectionSub.last_edit_time && (
        <p className="text-[10px] text-[#5C6B64] mt-1">
          Last edited: {fmtTimeIST(sectionSub.last_edit_time)}
        </p>
      )}
    </div>
  );
}

export default function ManagerDailyEntry() {
  const { user, getHeaders, API, canEditModule } = useAuth();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(JSON.parse(JSON.stringify(initialForm)));
  const initDate = searchParams.get('date');
  const editMode = searchParams.get('edit') === 'true';
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initDate) return new Date(initDate + 'T00:00:00');
    const d = new Date(); d.setDate(d.getDate() - 1); return d;
  });
  const [existingEntryId, setExistingEntryId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sectionSubmitting, setSectionSubmitting] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sections, setSections] = useState({ sales: true, kitchen: false, bar: false, other: false, cx: false, notes: false });
  const [mtdRating, setMtdRating] = useState({ avg_rating_mtd: 0, total_reviews_mtd: 0, entries_count: 0 });
  const [prevClosing, setPrevClosing] = useState({ kitchen: 0, bar: 0, found: false, fromDate: '' });
  const [sectionSubs, setSectionSubs] = useState({});
  const [editingSection, setEditingSection] = useState(null);
  const [prefill, setPrefill] = useState({ found: false, budget: null, last_year_sales: null });
  const [invConsumption, setInvConsumption] = useState(null);
  const [mtdSummary, setMtdSummary] = useState(null);
  // Track which sections were PIN-unlocked this session. Keyed by `${dateStr}:${sectionKey}`.
  // Used to show "Corrected via PIN" tag.
  const [pinUnlocked, setPinUnlocked] = useState({});

  // Override request state
  const [overrideRequests, setOverrideRequests] = useState([]);
  const [overrideDialog, setOverrideDialog] = useState({ open: false, sectionKey: '' });
  const [overrideReason, setOverrideReason] = useState('');

  // PIN dialog state (for override-approved past-date edits)
  const [pinDialog, setPinDialog] = useState({ open: false, sectionKey: '', action: '' });
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [overridePin, setOverridePin] = useState(''); // Stored after verification for edit flow

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', sectionKey: '' });

  // ITER243 — WinHMS POS import modal
  const [posImportOpen, setPosImportOpen] = useState(false);
  // ITER247 — Eligibility now keyed off the pos_type enum.
  // Only outlets explicitly tagged "winhms" surface the WinHMS importer button;
  // future POS adapters (ids/petpooja/other) flip on by simply adding their
  // matching case here and shipping the adapter. "none" hides the button.
  const posImportEligible = user?.pos_type === 'winhms';
  // Settlement breakdown captured by the importer (read-only, viewable + downloadable)
  const [settlement, setSettlement] = useState([]);
  const [posImportMeta, setPosImportMeta] = useState(null);
  // ITER246 — three read-only Occupancy KPIs displayed as cards in the Rooms
  // block. Auto-filled by /pos-import/apply; never editable.
  const [roomKpis, setRoomKpis] = useState({ arr: 0, revpar: 0, room_pax: 0 });

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
  const lastYearDateStr = `${parseInt(dateStr.slice(0, 4)) - 1}${dateStr.slice(4)}`;

  // Is this a past date (older than yesterday)?
  const yesterdayStr = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');
  const isPastDate = dateStr < yesterdayStr;

  // Derived: are budget / last_year_sales prefilled from system?
  const budgetPrefilled = prefill.found && prefill.budget != null;
  const lastYearPrefilled = prefill.found && prefill.last_year_sales != null;

  useEffect(() => {
    loadData();
    loadOverrideRequests();
  }, [dateStr]); // eslint-disable-line

  // Fetch MTD strip once on mount (doesn't change with selectedDate — always "till yesterday")
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/manager/mtd-summary`, { headers: getHeaders() });
        setMtdSummary(res.data);
      } catch {
        setMtdSummary(null);
      }
    })();
  }, []); // eslint-disable-line

  const loadOverrideRequests = async () => {
    try {
      const res = await axios.get(`${API}/override-requests/mine`, { headers: getHeaders() });
      setOverrideRequests(res.data);
    } catch { setOverrideRequests([]); }
  };

  const getOverrideStatus = (sectionKey) => {
    const req = overrideRequests.find(r =>
      r.date === dateStr && r.module === 'daily_entry' && r.section === sectionKey
    );
    return req?.status || null;
  };

  const handleRequestOverride = (sectionKey) => {
    setOverrideDialog({ open: true, sectionKey });
    setOverrideReason('');
  };

  const submitOverrideRequest = async () => {
    if (!overrideReason.trim()) { toast.error('Reason is mandatory'); return; }
    try {
      await axios.post(`${API}/override-requests`, {
        date: dateStr, module: 'daily_entry', section: overrideDialog.sectionKey, reason: overrideReason
      }, { headers: getHeaders() });
      toast.success('Override request submitted');
      setOverrideDialog({ open: false, sectionKey: '' });
      loadOverrideRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const loadData = async () => {
    loadMtdRating();
    loadPreviousClosing();
    loadInventoryConsumption();
    const prefillRes = await loadPrefill();
    await loadExisting(prefillRes);
  };

  const loadInventoryConsumption = async () => {
    try {
      const res = await axios.get(`${API}/inventory/consumption?date=${dateStr}`, { headers: getHeaders() });
      setInvConsumption(res.data);
    } catch { setInvConsumption(null); }
  };

  const loadMtdRating = async () => {
    try {
      const res = await axios.get(`${API}/daily-entries/mtd-rating`, { headers: getHeaders() });
      setMtdRating(res.data);
    } catch (err) { console.error('Failed to load MTD rating:', err); }
  };

  const loadPreviousClosing = async () => {
    try {
      const res = await axios.get(`${API}/daily-entries/previous-closing?date=${dateStr}`, { headers: getHeaders() });
      setPrevClosing({
        kitchen: res.data.kitchen_closing || 0,
        bar: res.data.bar_closing || 0,
        found: res.data.found || false,
        fromDate: res.data.from_date || ''
      });
    } catch {
      setPrevClosing({ kitchen: 0, bar: 0, found: false, fromDate: '' });
    }
  };

  const loadPrefill = async () => {
    try {
      const res = await axios.get(`${API}/manager/planning-prefill?date=${dateStr}`, { headers: getHeaders() });
      setPrefill(res.data);
      return res.data;
    } catch {
      const empty = { found: false, budget: null, last_year_sales: null };
      setPrefill(empty);
      return empty;
    }
  };

  const loadExisting = async (prefillData) => {
    try {
      const res = await axios.get(`${API}/daily-entries?date=${dateStr}`, { headers: getHeaders() });
      if (res.data.length > 0) {
        const entry = res.data[0];
        setExistingEntryId(entry.id);
        setSectionSubs(entry.section_submissions || {});
        // ITER243 — surface POS import settlement data (read-only)
        setSettlement(Array.isArray(entry.settlement_breakdown) ? entry.settlement_breakdown : []);
        setPosImportMeta(entry.pos_import_meta || null);
        // ITER246 — read-only Occupancy KPIs (ARR / RevPar / Room Pax)
        setRoomKpis({
          arr:      Number(entry.arr || 0),
          revpar:   Number(entry.revpar || 0),
          room_pax: Number(entry.room_pax || 0),
        });
        const loaded = { ...JSON.parse(JSON.stringify(initialForm)) };
        const simpleFields = ['budget','gross_sales','food_sale','beverage_sale','other_sale','number_of_covers',
          'channel_dine_in','channel_swiggy','channel_zomato','channel_other',
          'channel_dine_in_orders','channel_swiggy_orders','channel_zomato_orders','channel_other_orders',
          'lunch_sales','lunch_covers','dinner_sales','dinner_covers',
          'kitchen_issues','bar_issues','hk_issues','other_purchases','remarks','direct_reviews','avg_rating_day','nps_day','last_year_sales',
          'rooms_available','rooms_sold','room_revenue','room_revenue_budget','room_revenue_last_year'];
        for (const key of simpleFields) {
          // ITER244 — Preserve legitimately-stored 0 values explicitly as "0"
          // in the form. The previous `entry[key] !== 0 ? : ''` clause
          // blanked every 0 into a placeholder, which (a) made an unlocked
          // form look "reset" to managers and (b) risked overwriting
          // legitimate non-zero values with 0 if the manager hit Save Edit
          // without re-typing — because getSectionData reads `num('')` as 0.
          // Now only undefined/null fall back to '' (truly missing keys).
          loaded[key] = entry[key] !== undefined && entry[key] !== null
            ? String(entry[key]) : '';
        }
        for (const catKey of ['kitchen_total', 'bar_total']) {
          if (entry[catKey]) {
            loaded[catKey] = {
              opening: entry[catKey].opening != null ? String(entry[catKey].opening) : '',
              issues: entry[catKey].issues != null ? String(entry[catKey].issues) : '',
              closing: entry[catKey].closing != null ? String(entry[catKey].closing) : ''
            };
          }
        }
        // Apply prefill values if existing entry has empty budget/last_year_sales
        if (prefillData?.found) {
          if (!loaded.budget && prefillData.budget != null) loaded.budget = String(prefillData.budget);
          if (!loaded.last_year_sales && prefillData.last_year_sales != null) loaded.last_year_sales = String(prefillData.last_year_sales);
        }
        setForm(loaded);
      } else {
        setExistingEntryId(null);
        setSectionSubs({});
        setSettlement([]);
        setPosImportMeta(null);
        setRoomKpis({ arr: 0, revpar: 0, room_pax: 0 });
        const fresh = JSON.parse(JSON.stringify(initialForm));
        // Apply prefill values for new entry
        if (prefillData?.found) {
          if (prefillData.budget != null) fresh.budget = String(prefillData.budget);
          if (prefillData.last_year_sales != null) fresh.last_year_sales = String(prefillData.last_year_sales);
        }
        setForm(fresh);
      }
    } catch (err) { console.error('Failed to load existing entry:', err); }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateCategoryField = (category, field, value) =>
    setForm(prev => ({ ...prev, [category]: { ...prev[category], [field]: value } }));
  const toggleSection = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ITER243 — after POS import: snap visible form fields to canonical
  // prefill numbers. Manager still has to submit each section via the
  // normal flow (no auto-lock). `liquor_sale` + `settlement_breakdown`
  // were already persisted by the apply endpoint as additive fields.
  const applyPosImport = (resp) => {
    const p = resp?.prefill;
    if (!p) return;
    // Server-side: this returns the date that was reconciled (already
    // matches selectedDate when the manager picked today/yesterday in the
    // calendar before opening the modal). If the import date differs from
    // the currently selected one, switch the calendar to it so the manager
    // is editing the right day.
    if (p.date && p.date !== dateStr) {
      try { setSelectedDate(new Date(p.date + 'T00:00:00')); } catch { /* ignore */ }
    }
    setForm(prev => ({
      ...prev,
      gross_sales:      String(p.gross_sales ?? ''),
      food_sale:        String(p.food_sale ?? ''),
      beverage_sale:    String(p.beverage_sale ?? ''),
      number_of_covers: String(p.number_of_covers ?? ''),
      // ITER247 — Channel Split snap (Dine-in default; unblocks submit gate)
      channel_dine_in:        String(p.channel_dine_in ?? ''),
      channel_swiggy:         String(p.channel_swiggy ?? 0),
      channel_zomato:         String(p.channel_zomato ?? 0),
      channel_other:          String(p.channel_other ?? 0),
      channel_dine_in_orders: String(p.channel_dine_in_orders ?? ''),
      channel_swiggy_orders:  String(p.channel_swiggy_orders ?? 0),
      channel_zomato_orders:  String(p.channel_zomato_orders ?? 0),
      channel_other_orders:   String(p.channel_other_orders ?? 0),
      lunch_sales:      String(p.lunch_sales ?? ''),
      lunch_covers:     String(p.lunch_covers ?? ''),
      dinner_sales:     String(p.dinner_sales ?? ''),
      dinner_covers:    String(p.dinner_covers ?? ''),
      room_revenue:     String(p.room_revenue ?? ''),
      rooms_sold:       String(p.rooms_sold ?? ''),
      rooms_available:  String(p.rooms_available ?? ''),
    }));
    // ITER246 — snap the three read-only KPI cards immediately (without
    // waiting for the loadData round-trip).
    setRoomKpis({
      arr:      Number(p.arr ?? 0),
      revpar:   Number(p.revpar ?? 0),
      room_pax: Number(p.room_pax ?? 0),
    });
    // Reload the doc so existingEntryId + section_submissions reflect the upsert
    loadData();
  };

  const isSectionLocked = (sectionKey) => {
    if (editingSection === sectionKey) return false;
    return sectionSubs[sectionKey]?.locked === true;
  };

  const netSales = num(form.gross_sales);
  const channelTotal = num(form.channel_dine_in) + num(form.channel_swiggy) + num(form.channel_zomato) + num(form.channel_other);
  const salesSplitTotal = num(form.food_sale) + num(form.beverage_sale) + num(form.other_sale);
  const channelMatched = netSales > 0 && Math.abs(channelTotal - netSales) < 0.01;
  const salesSplitMatched = netSales > 0 && Math.abs(salesSplitTotal - netSales) < 0.01;
  const channelMismatch = netSales > 0 && !channelMatched;
  const salesSplitMismatch = netSales > 0 && !salesSplitMatched;
  const hasSalesMismatch = channelMismatch || salesSplitMismatch;
  const channelRemaining = netSales - channelTotal;
  const salesRemaining = netSales - salesSplitTotal;
  const kitchenOpening = existingEntryId && num(form.kitchen_total.opening) > 0 ? num(form.kitchen_total.opening) : (prevClosing.found ? prevClosing.kitchen : num(form.kitchen_total.opening));
  const barOpening = existingEntryId && num(form.bar_total.opening) > 0 ? num(form.bar_total.opening) : (prevClosing.found ? prevClosing.bar : num(form.bar_total.opening));
  const kitchenConsumption = kitchenOpening + num(form.kitchen_total.issues) - num(form.kitchen_total.closing);
  const barConsumption = barOpening + num(form.bar_total.issues) - num(form.bar_total.closing);
  const kitchenInvalid = false; // Inventory handles validation now
  const barInvalid = false;
  const foodSale = num(form.food_sale);
  const bevSale = num(form.beverage_sale);

  // Inventory-based consumption (single source of truth)
  const invKitchenConsumption = invConsumption?.kitchen?.consumption ?? 0;
  const invBarConsumption = invConsumption?.bar?.consumption ?? 0;
  const invKitchenOpening = invConsumption?.kitchen?.opening_value ?? 0;
  const invKitchenIssues = invConsumption?.kitchen?.issues ?? 0;
  const invKitchenClosing = invConsumption?.kitchen?.total_closing ?? 0;
  const invBarOpening = invConsumption?.bar?.opening_value ?? 0;
  const invBarIssues = invConsumption?.bar?.issues ?? 0;
  const invBarClosing = invConsumption?.bar?.total_closing ?? 0;
  // ITER249 — DISPLAY rounding only. Storage stays at 2dp; reconciliation
  // gates always run on the underlying values. These strings feed *display*
  // spans only.
  const foodCostPct = salesSplitMismatch ? null : (foodSale > 0 ? String(Math.round(invKitchenConsumption / foodSale * 100)) : '0');
  const bevCostPct = salesSplitMismatch ? null : (bevSale > 0 ? String(Math.round(invBarConsumption / bevSale * 100)) : '0');
  const covers = parseInt(form.number_of_covers) || 0;
  const foodApc = covers > 0 ? foodSale / covers : 0;
  const bevApc = covers > 0 ? bevSale / covers : 0;
  const totalApc = covers > 0 ? netSales / covers : 0;

  const getSectionData = (sectionKey) => {
    const fields = SECTION_FIELDS[sectionKey];
    const data = {};
    for (const f of fields) {
      if (f === 'kitchen_total') {
        data[f] = { opening: kitchenOpening, issues: num(form.kitchen_total.issues), closing: num(form.kitchen_total.closing) };
      } else if (f === 'bar_total') {
        data[f] = { opening: barOpening, issues: num(form.bar_total.issues), closing: num(form.bar_total.closing) };
      } else if (['direct_reviews', 'number_of_covers', 'lunch_covers', 'dinner_covers',
                   'channel_dine_in_orders', 'channel_swiggy_orders',
                   'channel_zomato_orders', 'channel_other_orders'].includes(f)) {
        data[f] = parseInt(form[f]) || 0;
      } else if (['avg_rating_day', 'nps_day'].includes(f)) {
        data[f] = parseFloat(form[f]) || 0;
      } else if (f === 'rooms_available' || f === 'rooms_sold') {
        data[f] = parseInt(form[f]) || 0;
      } else if (f === 'room_revenue' || f === 'room_revenue_budget' || f === 'room_revenue_last_year') {
        data[f] = num(form[f]);
      } else if (f === 'remarks') {
        data[f] = form[f] || '';
      } else if (f === 'discount') {
        data[f] = 0;
      } else if (f === 'net_sales_override') {
        data[f] = null;
      } else {
        data[f] = num(form[f]);
      }
    }
    return data;
  };

  const handleSectionSubmitClick = (sectionKey) => {
    setConfirmDialog({ open: true, type: 'submit', sectionKey });
  };

  const handleSectionEditClick = (sectionKey) => {
    const ss = sectionSubs[sectionKey];
    const editCount = ss?.edit_count || 0;
    setConfirmDialog({ open: true, type: 'edit', sectionKey, editCount });
  };

  const handleConfirm = async () => {
    const { type, sectionKey } = confirmDialog;
    setConfirmDialog({ open: false, type: '', sectionKey: '' });

    if (type === 'submit') {
      await doSubmitSection(sectionKey);
    } else if (type === 'edit') {
      setEditingSection(sectionKey);
      setSections(prev => {
        const sectionUIKey = { sales_data: 'sales', food_cost: 'kitchen', beverage_cost: 'bar', other_issues: 'other', customer_experience: 'cx', notes: 'notes' }[sectionKey];
        return { ...prev, [sectionUIKey]: true };
      });
    }
  };

  const doSubmitSection = async (sectionKey, pin = '') => {
    // Use stored override PIN if available and no explicit pin passed
    const effectivePin = pin || overridePin;
    setSectionSubmitting(sectionKey);
    try {
      const sectionData = getSectionData(sectionKey);
      const isEdit = editingSection === sectionKey;
      const url = isEdit ? `${API}/daily-entry/edit-section` : `${API}/daily-entry/submit-section`;
      const payload = {
        date: dateStr,
        section_name: sectionKey,
        data: sectionData
      };
      if (effectivePin) payload.pin = effectivePin;
      const res = await axios.post(url, payload, { headers: getHeaders() });

      if (res.data.id) setExistingEntryId(res.data.id);
      setSectionSubs(res.data.section_submissions || {});
      setEditingSection(null);
      setOverridePin(''); // Clear stored PIN after successful use
      toast.success(isEdit ? `${SECTION_LABELS[sectionKey]} updated` : `${SECTION_LABELS[sectionKey]} submitted`);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || 'Failed to submit';
      if (status === 428) {
        // PIN required — open PIN dialog
        setPinDialog({ open: true, sectionKey, action: editingSection === sectionKey ? 'edit' : 'submit' });
        setPinValue('');
        setPinError('');
      } else if (status === 403 && msg.includes('Invalid PIN')) {
        // PIN was wrong — re-open PIN dialog
        setPinDialog({ open: true, sectionKey, action: editingSection === sectionKey ? 'edit' : 'submit' });
        setPinValue('');
        setPinError('Invalid PIN — try again');
      } else if (status === 409) {
        // Tier 3 — edit limit reached; prompt for override request.
        toast.info(msg, {
          action: {
            label: 'Request Override',
            onClick: () => handleRequestOverride(sectionKey),
          },
          duration: 8000,
        });
        await loadExisting();
      } else {
        toast.error(msg);
      }
    } finally {
      setSectionSubmitting('');
    }
  };

  const handleEditSave = (sectionKey) => {
    doSubmitSection(sectionKey);
  };

  const handlePinConfirm = async () => {
    if (!pinValue || pinValue.length !== 4) {
      setPinError('Enter a 4-digit PIN');
      return;
    }
    // Verify PIN first via backend (with audit context)
    try {
      await axios.post(`${API}/auth/verify-pin`, {
        pin: pinValue,
        module: 'daily_business_report',
        outlet_id: user?.outlet_id,
        entry_date: dateStr,
        section: pinDialog.sectionKey,
      }, { headers: getHeaders() });
    } catch (err) {
      setPinError(err.response?.data?.detail || 'Invalid PIN');
      return;
    }
    setPinError('');
    const { sectionKey, action } = pinDialog;
    setPinDialog({ open: false, sectionKey: '', action: '' });
    // Mark this (date, section) as PIN-corrected for UI indication.
    setPinUnlocked(prev => ({ ...prev, [`${dateStr}:${sectionKey}`]: true }));

    if (action === 'edit') {
      // ITER253 — Open a backend-side edit session so the POS-import
      // `/apply` guard ALSO recognises this section as unlocked. Until
      // this call, only the React tree knew the user was editing; the
      // import guard kept blocking. /edit-section + /pos-import/apply
      // both re-lock on save, so the unlock window is bounded.
      try {
        await axios.post(`${API}/daily-entry/begin-edit-session`, {
          date: dateStr,
          section_name: sectionKey,
          pin: pinValue,
        }, { headers: getHeaders() });
      } catch (err) {
        // If the section was never persisted (no entry yet) or the
        // server reports another non-fatal state, fall through — the
        // user can still attempt the edit; the existing edit/save
        // endpoints carry their own guards.
        const detail = err?.response?.data?.detail;
        if (detail) console.warn('begin-edit-session note:', detail);
      }
      // Store PIN and unlock section for editing — user makes changes, then clicks Save Edit
      setOverridePin(pinValue);
      setEditingSection(sectionKey);
      setSections(prev => {
        const sectionUIKey = { sales_data: 'sales', food_cost: 'kitchen', beverage_cost: 'bar', other_issues: 'other', customer_experience: 'cx', notes: 'notes' }[sectionKey];
        return { ...prev, [sectionUIKey]: true };
      });
      // Reload from server so the local section_submissions reflects the
      // freshly-unlocked state — this is what re-enables the
      // "Import from WinHMS" button against this section.
      try { await loadData(); } catch (e) { /* noop */ }
      toast.success('PIN verified — edit the section and save');
    } else {
      // Direct submit with PIN
      await doSubmitSection(sectionKey, pinValue);
    }
  };

  const handlePinEdit = (sectionKey) => {
    setPinDialog({ open: true, sectionKey, action: 'edit' });
    setPinValue('');
    setPinError('');
  };

  const preparePayload = () => {
    const payload = { date: dateStr };
    payload.budget = num(form.budget);
    payload.gross_sales = num(form.gross_sales);
    payload.discount = 0;
    payload.net_sales_override = null;
    payload.food_sale = num(form.food_sale);
    payload.beverage_sale = num(form.beverage_sale);
    payload.other_sale = num(form.other_sale);
    payload.number_of_covers = parseInt(form.number_of_covers) || 0;
    payload.channel_dine_in = num(form.channel_dine_in);
    payload.channel_swiggy = num(form.channel_swiggy);
    payload.channel_zomato = num(form.channel_zomato);
    payload.channel_other = num(form.channel_other);
    payload.channel_dine_in_orders = parseInt(form.channel_dine_in_orders) || 0;
    payload.channel_swiggy_orders = parseInt(form.channel_swiggy_orders) || 0;
    payload.channel_zomato_orders = parseInt(form.channel_zomato_orders) || 0;
    payload.channel_other_orders = parseInt(form.channel_other_orders) || 0;
    payload.lunch_sales = num(form.lunch_sales);
    payload.lunch_covers = parseInt(form.lunch_covers) || 0;
    payload.dinner_sales = num(form.dinner_sales);
    payload.dinner_covers = parseInt(form.dinner_covers) || 0;
    payload.kitchen_total = { opening: kitchenOpening, issues: num(form.kitchen_total.issues), closing: num(form.kitchen_total.closing) };
    payload.bar_total = { opening: barOpening, issues: num(form.bar_total.issues), closing: num(form.bar_total.closing) };
    payload.kitchen_issues = num(form.kitchen_issues);
    payload.bar_issues = num(form.bar_issues);
    payload.hk_issues = num(form.hk_issues);
    payload.other_purchases = num(form.other_purchases);
    payload.remarks = form.remarks;
    payload.direct_reviews = parseInt(form.direct_reviews) || 0;
    payload.avg_rating_day = parseFloat(form.avg_rating_day) || 0;
    payload.nps_day = parseFloat(form.nps_day) || 0;
    payload.last_year_sales = num(form.last_year_sales);
    payload.rooms_available = parseInt(form.rooms_available) || 0;
    payload.rooms_sold = parseInt(form.rooms_sold) || 0;
    payload.room_revenue = num(form.room_revenue);
    payload.room_revenue_budget = num(form.room_revenue_budget);
    payload.room_revenue_last_year = num(form.room_revenue_last_year);
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.budget || !form.gross_sales) {
      toast.error('Budget and Net Sales are required');
      return;
    }
    if (hasSalesMismatch) {
      toast.error('Sales split must match Net Sales before submission');
      return;
    }
    setSubmitting(true);
    try {
      const payload = preparePayload();
      if (existingEntryId) {
        await axios.put(`${API}/daily-entries/${existingEntryId}`, payload, { headers: getHeaders() });
        toast.success('Entry updated successfully');
      } else {
        const res = await axios.post(`${API}/daily-entries`, payload, { headers: getHeaders() });
        setExistingEntryId(res.data.id);
        toast.success('Entry submitted successfully');
      }
      loadMtdRating();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const canEdit = true;
  const salesLocked = isSectionLocked('sales_data');
  const foodLocked = isSectionLocked('food_cost');
  const bevLocked = isSectionLocked('beverage_cost');
  const otherLocked = isSectionLocked('other_issues');
  const cxLocked = isSectionLocked('customer_experience');
  const notesLocked = isSectionLocked('notes');
  const roomsEnabled = user?.rooms_enabled === true;

  // Room auto-calculations
  const roomsSold = parseInt(form.rooms_sold) || 0;
  const roomsAvail = parseInt(form.rooms_available) || 0;
  const roomRev = num(form.room_revenue);
  const occupancyPct = roomsAvail > 0 ? String(Math.round(roomsSold / roomsAvail * 100)) : '—';
  const arrCalc = roomsSold > 0 ? fmt(roomRev / roomsSold) : '—';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7F6F1] via-[#FAFAF5] to-[#F9FAF6]">
      <div className="w-full" data-testid="manager-daily-entry-form">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Daily Business Report</h2>
          <p className="text-sm text-[#5C6B64] mt-1">{user?.outlet_name || 'Your Outlet'}</p>
        </div>

        {/* Top-right cluster: MTD strip + date controls */}
        <div className="flex flex-col items-stretch lg:items-end gap-2">
          {/* Compact financial summary strip — single row */}
          {mtdSummary && (
            <>
              <div
                className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-[#1A2421]"
                data-testid="mtd-summary-strip"
                title={`Reference: ${format(new Date(mtdSummary.period_start + 'T00:00:00'), 'MMM yyyy')} — day ${mtdSummary.days_elapsed} of ${mtdSummary.days_in_month}`}
              >
                <div className="flex items-center gap-1.5" data-testid="mtd-summary-monthly-budget">
                  <span className="uppercase tracking-[0.06em] text-[10px] text-[#1A2421] font-medium">Monthly Budget</span>
                  <span className="font-semibold tabular-nums text-[#1A2421]">{fmt(mtdSummary.monthly_budget)}</span>
                </div>
                <span className="w-px h-3 bg-[#5C6B64] hidden sm:block" aria-hidden />
                <div
                  className="flex items-center gap-1.5"
                  data-testid="mtd-summary-mtd-budget"
                  title={`Prorated: (Monthly Budget ÷ ${mtdSummary.days_in_month} days) × ${mtdSummary.days_elapsed} days elapsed`
                    + `\nActual (sum of planning rows 1→yesterday): ${fmt(mtdSummary.mtd_budget_actual)}`}
                >
                  <span className="uppercase tracking-[0.06em] text-[10px] text-[#1A2421] font-medium">MTD Budget</span>
                  <span className="font-semibold tabular-nums text-[#1A2421]">{fmt(mtdSummary.mtd_budget)}</span>
                  <span className="text-[9px] text-[#1A2421] italic">(prorated)</span>
                </div>
                <span className="w-px h-3 bg-[#5C6B64] hidden sm:block" aria-hidden />
                <div className="flex items-center gap-1.5" data-testid="mtd-summary-mtd-sales">
                  <span className="uppercase tracking-[0.06em] text-[10px] text-[#1A2421] font-medium">MTD Sales (Till Yesterday)</span>
                  <span className="font-semibold tabular-nums text-[#1A2421]">{fmt(mtdSummary.mtd_sales)}</span>
                </div>
              </div>
              {/* Previous Month subline — shown when no days elapsed yet in current month */}
              {mtdSummary.days_elapsed === 0 && mtdSummary.previous_month && (
                <div
                  className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-[#1A2421]"
                  data-testid="mtd-summary-previous-month"
                >
                  <span className="uppercase tracking-[0.06em] font-medium">
                    Previous Month ({format(new Date(mtdSummary.previous_month.year, mtdSummary.previous_month.month - 1, 1), 'MMM yyyy')})
                  </span>
                  <span data-testid="mtd-summary-previous-month-budget">
                    Budget <span className="tabular-nums font-semibold">{fmt(mtdSummary.previous_month.monthly_budget)}</span>
                  </span>
                  <span data-testid="mtd-summary-previous-month-sales">
                    Sales <span className="tabular-nums font-semibold">{fmt(mtdSummary.previous_month.total_sales)}</span>
                  </span>
                </div>
              )}
            </>
          )}

          {/* Existing date controls */}
          <div className="flex items-center gap-3">
            {existingEntryId && (
              <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20" data-testid="entry-exists-badge">
                Entry Exists
              </Badge>
            )}
            {posImportEligible && (
              <Button
                variant="outline" size="sm"
                onClick={() => setPosImportOpen(true)}
                className="border-[color:var(--color-primary)]/30 text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)]/5 h-10"
                data-testid="pos-import-open-btn"
              >
                <Upload className="w-4 h-4 mr-1.5" />
                Import from WinHMS
              </Button>
            )}
            <Button variant="outline" size="sm"
              onClick={() => { const y = new Date(); y.setDate(y.getDate() - 1); setSelectedDate(y); setEditingSection(null); }}
              className={`border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F3F5F2] h-10 ${
                format(selectedDate, 'yyyy-MM-dd') === format((() => { const y = new Date(); y.setDate(y.getDate() - 1); return y; })(), 'yyyy-MM-dd')
                  ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : ''
              }`}
              data-testid="entry-yesterday-btn">
              Yesterday
            </Button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 border-[#E2E8DF] text-[#1A2421] hover:bg-[#F3F5F2]" data-testid="date-picker-trigger">
                  <CalendarIcon className="w-4 h-4 mr-2 text-[#5C6B64]" />
                  {format(selectedDate, 'dd MMM yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="single" selected={selectedDate}
                  onSelect={(d) => { if (d) { setSelectedDate(d); setCalendarOpen(false); setEditingSection(null); } }}
                  disabled={(d) => d > new Date()} />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Inventory cutover indicator */}
      {dateStr >= '2026-04-14' && (
        <div className="flex items-center gap-2 px-3 py-2 mb-4 bg-[#2D6A4F]/10 border border-[#2D6A4F]/20 rounded-lg" data-testid="inventory-cutover-banner">
          <CheckCircle2 className="w-4 h-4 text-[#2D6A4F] shrink-0" />
          <p className="text-xs text-[#2D6A4F]">Inventory-based costing active from 14 April — Food & Beverage costs are calculated from inventory</p>
        </div>
      )}

      {/* ITER258 — The form-level onSubmit used to fire on Enter-key in
          any input, calling PUT /daily-entries/{id} which bypassed the
          section lock/PIN state machine and (pre-ITER258) zeroed
          calculated.room_revenue by stripping unknown fields via
          Pydantic. The section-level Submit/Save Edit buttons inside
          each card are the canonical commit path; pressing Enter must
          NOT escape that. We swallow the form submit at the boundary;
          if a legitimate entry-wide save is ever needed it must go
          through the section-level endpoints. */}
      <form onSubmit={(e) => { e.preventDefault(); }} className="space-y-6"
            data-testid="daily-entry-form">
        {/* Sales Section */}
        <Card className={`border-[#E2E8DF] shadow-sm overflow-hidden ${salesLocked ? 'border-l-4 border-l-[#2D6A4F]' : ''}`}>
          <SectionHeader icon={ShoppingCart} title="Sales Data" open={sections.sales} onToggle={() => toggleSection('sales')}
            sectionStatus={sectionSubs.sales_data || { locked: false }}
            pinCorrected={pinUnlocked[`${dateStr}:sales_data`]} />
          {sections.sales && (
            <CardContent className="px-5 pt-0 pb-5 section-enter">
              {/* ITER250 — Sales card has two view states. When the section is
                  locked (post-import/submit), render the calm read-only summary.
                  When PIN-unlocked (editingSection==='sales_data' → salesLocked
                  is false) render the existing input form unchanged. */}
              {salesLocked ? (
                <SalesReadView
                  form={form} prefill={prefill} lastYearDateStr={lastYearDateStr}
                  netSales={netSales} channelMatched={channelMatched}
                  salesSplitMatched={salesSplitMatched} hasSalesMismatch={hasSalesMismatch}
                  covers={covers} foodApc={foodApc} bevApc={bevApc} totalApc={totalApc}
                  roomsEnabled={roomsEnabled} roomsSold={roomsSold} roomsAvail={roomsAvail}
                  roomRev={roomRev} occupancyPct={occupancyPct} roomKpis={roomKpis} />
              ) : (<>
              {/* ITER256 — Stranded-state banner. When the section is
                  unlocked but already has prior submission/import data
                  (i.e. begin-edit-session was called and the user
                  abandoned the save), the dashboard hides this day
                  until the section is re-locked. Surface that loudly
                  so the manager either re-submits or knows to act. */}
              {(() => {
                const ss = sectionSubs.sales_data || {};
                const hasPriorReport = !!ss.status && !ss.locked
                  && (Number(form.gross_sales) > 0 || Number(form.food_sale) > 0
                      || Number(form.room_revenue) > 0);
                if (!hasPriorReport) return null;
                return (
                  <div
                    className="mb-4 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                    data-testid="sales-stranded-banner">
                    <strong>Section unlocked — not reported.</strong>{' '}
                    The dashboard is currently hiding this day. Click{' '}
                    <strong>Save Edit</strong> below to re-lock the section
                    and restore it to the dashboard.
                  </div>
                );
              })()}
              {/* Net Sales — primary KPI, full-width, prominent */}
              <div className="mb-4">
                <NumberInput
                  label="Net Sales"
                  value={form.gross_sales}
                  onChange={v => updateField('gross_sales', v)}
                  required
                  testId="gross-sales-input"
                  disabled={salesLocked}
                  size="big"
                />
              </div>

              {/* Budget — secondary, smaller */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[13px] font-medium text-[#1A2421]">Budget<span className="text-[#C45A41]"> *</span></Label>
                  {budgetPrefilled && (
                    <span className="text-[10px] text-[#2D6A4F] font-medium flex items-center gap-1" data-testid="budget-prefill-indicator">
                      <Lock className="w-3 h-3" /> Pre-filled
                    </span>
                  )}
                </div>
                <Input
                  data-testid="budget-input"
                  type="number" inputMode="decimal" step="any" placeholder="0"
                  value={form.budget}
                  onChange={e => updateField('budget', e.target.value)}
                  disabled={salesLocked || budgetPrefilled}
                  className={`mt-1.5 h-10 bg-white border border-[#1A2421]/15 hover:border-[#1A2421]/30 focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] text-sm ${(salesLocked || budgetPrefilled) ? 'bg-[#F3F5F2] cursor-not-allowed opacity-70' : ''}`}
                />
              </div>

              {/* Food | Beverage | Other Sale — same row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <NumberInput label="Food Sale" value={form.food_sale} onChange={v => updateField('food_sale', v)} testId="food-sale-input" disabled={salesLocked} />
                <NumberInput label="Beverage Sale" value={form.beverage_sale} onChange={v => updateField('beverage_sale', v)} testId="beverage-sale-input" disabled={salesLocked} />
                <NumberInput label="Other Sale" value={form.other_sale} onChange={v => updateField('other_sale', v)} testId="other-sale-input" disabled={salesLocked} />
              </div>

              {/* Sales Split — subtle info bar */}
              {netSales > 0 && (
                <div className={`px-3 py-2 rounded-md text-xs font-medium flex items-center justify-between border ${salesSplitMatched ? 'bg-[#2D6A4F]/5 text-[#2D6A4F] border-[#2D6A4F]/20' : 'bg-[#C45A41]/5 text-[#C45A41] border-[#C45A41]/20'}`}
                  data-testid="sales-split-validation">
                  <span>Sales Split: {fmt(salesSplitTotal)} / {fmt(netSales)}</span>
                  {salesSplitMatched ? (
                    <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Matched</span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> {salesRemaining > 0 ? `Remaining: ${fmt(salesRemaining)}` : `Over by: ${fmt(Math.abs(salesRemaining))}`}
                    </span>
                  )}
                </div>
              )}

              {/* Last-year comparison — subtle panel */}
              <div className="mt-4 p-3 bg-[color:var(--color-primary)]/[0.035] border border-[color:var(--color-primary)]/10 rounded-lg" data-testid="last-year-sales-section">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] text-[#5C6B64] uppercase tracking-[0.05em] font-medium">
                    Same Date ({lastYearDateStr}{prefill.last_year_same_date_day ? `, ${prefill.last_year_same_date_day}` : ''})
                  </span>
                  <div className="flex items-center gap-2">
                    {lastYearPrefilled && (
                      <span className="text-[10px] text-[#2D6A4F] font-medium flex items-center gap-1" data-testid="last-year-prefill-indicator">
                        <Lock className="w-3 h-3" /> Pre-filled
                      </span>
                    )}
                    {num(form.last_year_sales) > 0 && netSales > 0 && (
                      <span className={`text-xs font-medium ${netSales >= num(form.last_year_sales) ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                        data-testid="yoy-same-date-pct">
                        YoY: {`${netSales >= num(form.last_year_sales) ? '+' : ''}${Math.round((netSales - num(form.last_year_sales)) / num(form.last_year_sales) * 100)}%`}
                      </span>
                    )}
                  </div>
                </div>
                <Input data-testid="last-year-sales-input" type="number" inputMode="decimal" step="any"
                  placeholder="Last year's sale from POS" value={form.last_year_sales}
                  onChange={e => updateField('last_year_sales', e.target.value)} disabled={salesLocked || lastYearPrefilled}
                  className={`h-10 bg-white border border-[#1A2421]/15 focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] text-sm ${(salesLocked || lastYearPrefilled) ? 'bg-[#F3F5F2] cursor-not-allowed opacity-70' : ''}`} />

                {prefill.last_year_same_day_date && (
                  <div className="mt-3 pt-3 border-t border-[color:var(--color-primary)]/10" data-testid="same-day-weekday-section">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-[#5C6B64] uppercase tracking-[0.05em] font-medium">
                        Same Day — {prefill.last_year_same_day_name} ({prefill.last_year_same_day_date})
                      </span>
                      {prefill.last_year_same_day_sales != null && netSales > 0 && (
                        <span className={`text-xs font-medium ${netSales >= prefill.last_year_same_day_sales ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                          data-testid="yoy-same-day-pct">
                          YoY: {`${netSales >= prefill.last_year_same_day_sales ? '+' : ''}${Math.round((netSales - prefill.last_year_same_day_sales) / prefill.last_year_same_day_sales * 100)}%`}
                        </span>
                      )}
                    </div>
                    <p className="text-base font-semibold text-[#1A2421]" data-testid="same-day-sales-value">
                      {prefill.last_year_same_day_sales != null ? `${fmt(prefill.last_year_same_day_sales)}` : <span className="text-sm text-[#5C6B64] font-normal">No data available</span>}
                    </p>
                  </div>
                )}
              </div>

              {/* Covers — clean separate row */}
              <div className="mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <NumberInput label="Number of Covers" value={form.number_of_covers} onChange={v => updateField('number_of_covers', v)} required testId="covers-input" placeholder="0" disabled={salesLocked} />
                </div>
                {covers > 0 && (
                  <div className="mt-3 p-3 bg-[color:var(--color-primary)]/[0.04] border border-[color:var(--color-primary)]/10 rounded-lg">
                    <p className="text-[10px] uppercase tracking-[0.05em] text-[#5C6B64] font-semibold mb-2">APC (Average Per Cover)</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-[10px] text-[#5C6B64]">Food APC</p>
                        <p className="text-sm font-semibold text-[color:var(--color-primary)]" data-testid="food-apc-display">{fmt(foodApc)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#5C6B64]">Bev APC</p>
                        <p className="text-sm font-semibold text-[color:var(--color-primary)]" data-testid="bev-apc-display">{fmt(bevApc)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#5C6B64]">Total APC</p>
                        <p className="text-sm font-semibold text-[color:var(--color-primary)]" data-testid="total-apc-display">{fmt(totalApc)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-[#1A2421]">Channel Split</span>
                  {!salesLocked && netSales > 0 && channelMismatch && channelRemaining > 0 && (
                    <Button type="button" variant="ghost" size="sm"
                      className="h-7 text-xs text-[#2D6A4F] hover:text-[color:var(--color-primary)] hover:bg-[#2D6A4F]/10"
                      data-testid="auto-fill-channel-other"
                      onClick={() => updateField('channel_other', String(num(form.channel_other) + channelRemaining))}>
                      Auto-fill Other (+{fmt(channelRemaining)})
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <NumberInput label="Dine-in" value={form.channel_dine_in} onChange={v => updateField('channel_dine_in', v)} testId="channel-dine-in-input" disabled={salesLocked} />
                  <NumberInput label="Swiggy" value={form.channel_swiggy} onChange={v => updateField('channel_swiggy', v)} testId="channel-swiggy-input" disabled={salesLocked} />
                  <NumberInput label="Zomato" value={form.channel_zomato} onChange={v => updateField('channel_zomato', v)} testId="channel-zomato-input" disabled={salesLocked} />
                  <NumberInput label="Other" value={form.channel_other} onChange={v => updateField('channel_other', v)} testId="channel-other-input" disabled={salesLocked} />
                </div>

                {/* Channel-wise order counts (mandatory when matching channel sales > 0) */}
                <div className="mt-3 pt-3 border-t border-[color:var(--color-primary)]/10">
                  <p className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold mb-2">Channel Orders <span className="text-[#C45A41] normal-case font-normal">(required when channel sales &gt; 0)</span></p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <NumberInput
                      label={`Dine-in${num(form.channel_dine_in) > 0 ? ' *' : ''}`}
                      value={form.channel_dine_in_orders}
                      onChange={v => updateField('channel_dine_in_orders', v)}
                      testId="channel-dine-in-orders" placeholder="0" disabled={salesLocked} />
                    <NumberInput
                      label={`Swiggy${num(form.channel_swiggy) > 0 ? ' *' : ''}`}
                      value={form.channel_swiggy_orders}
                      onChange={v => updateField('channel_swiggy_orders', v)}
                      testId="channel-swiggy-orders" placeholder="0" disabled={salesLocked} />
                    <NumberInput
                      label={`Zomato${num(form.channel_zomato) > 0 ? ' *' : ''}`}
                      value={form.channel_zomato_orders}
                      onChange={v => updateField('channel_zomato_orders', v)}
                      testId="channel-zomato-orders" placeholder="0" disabled={salesLocked} />
                    <NumberInput
                      label={`Other${num(form.channel_other) > 0 ? ' *' : ''}`}
                      value={form.channel_other_orders}
                      onChange={v => updateField('channel_other_orders', v)}
                      testId="channel-other-orders" placeholder="0" disabled={salesLocked} />
                  </div>
                </div>

                {/* Service-period split: Lunch + Dinner */}
                <div className="mt-3 pt-3 border-t border-[color:var(--color-primary)]/10">
                  <p className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold mb-2">Service Split <span className="normal-case font-normal text-[#5C6B64]">(optional but reconciled with totals)</span></p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <NumberInput label="Lunch Sales (₹)" value={form.lunch_sales} onChange={v => updateField('lunch_sales', v)} testId="lunch-sales" placeholder="0" disabled={salesLocked} />
                    <NumberInput label="Lunch Covers" value={form.lunch_covers} onChange={v => updateField('lunch_covers', v)} testId="lunch-covers" placeholder="0" disabled={salesLocked} />
                    <NumberInput label="Dinner Sales (₹)" value={form.dinner_sales} onChange={v => updateField('dinner_sales', v)} testId="dinner-sales" placeholder="0" disabled={salesLocked} />
                    <NumberInput label="Dinner Covers" value={form.dinner_covers} onChange={v => updateField('dinner_covers', v)} testId="dinner-covers" placeholder="0" disabled={salesLocked} />
                  </div>
                  {(num(form.lunch_sales) + num(form.dinner_sales) > 0) && (() => {
                    const lds = num(form.lunch_sales) + num(form.dinner_sales);
                    const gross = num(form.gross_sales);
                    const lc = num(form.lunch_covers) + num(form.dinner_covers);
                    const tot = num(form.number_of_covers);
                    const sOK = gross === 0 || lds <= gross * 1.001;
                    const cOK = tot === 0 || lc === 0 || lc <= tot;
                    const ok = sOK && cOK;
                    return (
                      <div className={`mt-2 px-3 py-2 rounded-lg text-xs font-medium flex items-center justify-between ${ok ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#C45A41]/10 text-[#C45A41]'}`} data-testid="service-split-validation">
                        <span>Lunch + Dinner: {fmt(lds)} / {fmt(gross)} · Covers {lc} / {tot || '—'}</span>
                        {ok ? <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span> : <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Mismatch</span>}
                      </div>
                    );
                  })()}
                </div>
                {netSales > 0 && (
                  <div className={`mt-2 px-3 py-2 rounded-lg text-xs font-medium flex items-center justify-between ${channelMatched ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#C45A41]/10 text-[#C45A41]'}`}
                    data-testid="channel-split-validation">
                    <span>Channel Split: {fmt(channelTotal)} / {fmt(netSales)}</span>
                    {channelMatched ? (
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Matched</span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> {channelRemaining > 0 ? `Remaining: ${fmt(channelRemaining)}` : `Over by: ${fmt(Math.abs(channelRemaining))}`}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* Rooms (within Sales — only if outlet has rooms enabled) */}
              {roomsEnabled && (
                <div className="mt-4 pt-4 border-t border-[color:var(--color-primary)]/10" data-testid="rooms-in-sales-section">
                  <div className="flex items-center gap-2 mb-3">
                    <BedDouble className="w-4 h-4 text-[color:var(--color-primary)]" />
                    <span className="text-sm font-medium text-[#1A2421]">Rooms</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <NumberInput label="Rooms Available" value={form.rooms_available}
                      onChange={v => updateField('rooms_available', v)}
                      testId="rooms-available-input" disabled={salesLocked} placeholder="0" />
                    <NumberInput label="Rooms Sold" value={form.rooms_sold}
                      onChange={v => updateField('rooms_sold', v)}
                      testId="rooms-sold-input" disabled={salesLocked} placeholder="0" />
                    <NumberInput label="Room Revenue (₹)" value={form.room_revenue}
                      onChange={v => updateField('room_revenue', v)}
                      testId="room-revenue-input" disabled={salesLocked} placeholder="0" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <NumberInput label="Room Rev. Budget (₹)" value={form.room_revenue_budget}
                      onChange={v => updateField('room_revenue_budget', v)}
                      testId="room-budget-input" disabled={salesLocked} placeholder="0" />
                    {/* ITER246 — Room Rev. Last Year is FROZEN. Display-only.
                        Not even editable after PIN-unlock — last year is auto-
                        derived from history; manual entry is never appropriate. */}
                    <div className="space-y-1.5" data-testid="room-ly-readonly-wrapper">
                      <Label className="text-[13px] font-medium text-[#1A2421]">
                        Room Rev. Last Year (₹)
                        <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-medium text-[#5C6B64] uppercase tracking-wider align-middle">
                          <Lock className="w-2.5 h-2.5" /> auto
                        </span>
                      </Label>
                      <div data-testid="room-ly-readonly"
                        className="h-10 px-3 flex items-center bg-[#F3F5F2] border border-[#1A2421]/10 rounded-md text-sm tabular-nums text-[#5C6B64]">
                        {num(form.room_revenue_last_year) > 0
                          ? fmt(form.room_revenue_last_year)
                          : 'No prior-year data yet'}
                      </div>
                    </div>
                  </div>
                  {(roomsSold > 0 || roomsAvail > 0) && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                        <p className="text-[10px] text-[#5C6B64] uppercase">Occupancy %</p>
                        <p className="text-sm font-bold text-[color:var(--color-primary)]" data-testid="rooms-occupancy-calc">{occupancyPct}%</p>
                      </div>
                      <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                        <p className="text-[10px] text-[#5C6B64] uppercase">ARR (calc)</p>
                        <p className="text-sm font-bold text-[color:var(--color-primary)]" data-testid="rooms-arr-calc">{arrCalc}</p>
                      </div>
                    </div>
                  )}
                  {/* ITER246 — three read-only Occupancy KPIs from WinHMS.
                      Auto-filled from the "For the Day" row; never editable. */}
                  {(roomKpis.arr > 0 || roomKpis.revpar > 0 || roomKpis.room_pax > 0) && (
                    <div className="grid grid-cols-3 gap-3 mt-3" data-testid="room-kpis-from-winhms">
                      <div className="p-2.5 bg-[color:var(--color-primary)]/5 border border-[color:var(--color-primary)]/15 rounded-lg text-center">
                        <p className="text-[10px] text-[#5C6B64] uppercase tracking-wider">ARR (WinHMS)</p>
                        <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums" data-testid="kpi-arr">
                          {fmt(roomKpis.arr)}
                        </p>
                      </div>
                      <div className="p-2.5 bg-[color:var(--color-primary)]/5 border border-[color:var(--color-primary)]/15 rounded-lg text-center">
                        <p className="text-[10px] text-[#5C6B64] uppercase tracking-wider">RevPar</p>
                        <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums" data-testid="kpi-revpar">
                          {fmt(roomKpis.revpar)}
                        </p>
                      </div>
                      <div className="p-2.5 bg-[color:var(--color-primary)]/5 border border-[color:var(--color-primary)]/15 rounded-lg text-center">
                        <p className="text-[10px] text-[#5C6B64] uppercase tracking-wider">Room Pax</p>
                        <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums" data-testid="kpi-room-pax">
                          {roomKpis.room_pax}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              </>)}
              {editingSection === 'sales_data' ? (
                <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
                  <Button onClick={() => handleEditSave('sales_data')} disabled={!!sectionSubmitting || hasSalesMismatch}
                    className="w-full h-10 bg-[#D4A017] hover:bg-[#B8901A] text-white text-sm"
                    data-testid="save-edit-sales_data">
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    {sectionSubmitting === 'sales_data' ? 'Saving...' : 'Save Edit'}
                  </Button>
                  {hasSalesMismatch && (
                    <p className="text-xs text-[#C45A41] mt-2 text-center" data-testid="mismatch-block-msg">
                      Total must match Net Sales before submission
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <SectionSubmitBar sectionKey="sales_data" sectionSub={sectionSubs.sales_data}
                    onSubmit={handleSectionSubmitClick} onEdit={handleSectionEditClick}
                    submitting={sectionSubmitting === 'sales_data'} disabled={hasSalesMismatch} editMode={editMode}
                    onRequestOverride={handleRequestOverride} overrideStatus={getOverrideStatus('sales_data')}
                    moduleWritable={canEditModule('daily_entry')}
                    isPastDate={isPastDate} pinSet={user?.pin_set} onPinEdit={handlePinEdit} />
                  {hasSalesMismatch && !salesLocked && (
                    <p className="text-xs text-[#C45A41] mt-1 text-center px-4 pb-3" data-testid="mismatch-block-msg">
                      Total must match Net Sales before submission
                    </p>
                  )}
                </>
              )}
            </CardContent>
          )}
        </Card>

        {/* Inventory Cost — compact info cards (not collapsible) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-[#E2E8DF] shadow-sm overflow-hidden" data-testid="kitchen-closing-section">
            <CardContent className="px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ChefHat className="w-4 h-4 text-[color:var(--color-primary)]" strokeWidth={1.75} />
                  <h3 className="text-sm font-semibold text-[#1A2421]">Food Cost</h3>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-[#5C6B64] bg-[#F3F5F2] px-2 py-0.5 rounded-full font-medium">From Inventory</span>
              </div>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Consumption</p>
                  <p className={`text-2xl font-bold tabular-nums ${invKitchenConsumption < 0 ? 'text-[#C45A41]' : 'text-[color:var(--color-primary)]'}`}
                    data-testid="kitchen-consumption-display">{fmt(invKitchenConsumption)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Food Cost %</p>
                  {foodCostPct !== null ? (
                    <p className={`text-lg font-bold tabular-nums ${parseFloat(foodCostPct) > 35 ? 'text-[#C45A41]' : 'text-[color:var(--color-primary)]'}`}
                      data-testid="food-cost-pct-display">{foodCostPct}%</p>
                  ) : (
                    <p className="text-[11px] text-[#5C6B64]">Enter sales split</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-[#E2E8DF]">
                <div>
                  <p className="text-[9px] text-[#5C6B64] uppercase">Opening</p>
                  <p className="text-xs font-semibold text-[#1A2421] tabular-nums" data-testid="inv-kitchen-opening">{fmt(invKitchenOpening)}</p>
                </div>
                <div>
                  <p className="text-[9px] text-[#5C6B64] uppercase">Issues</p>
                  <p className="text-xs font-semibold text-amber-600 tabular-nums" data-testid="inv-kitchen-issues">{fmt(invKitchenIssues)}</p>
                </div>
                <div>
                  <p className="text-[9px] text-[#5C6B64] uppercase">Closing</p>
                  <p className="text-xs font-semibold text-[#1A2421] tabular-nums" data-testid="inv-kitchen-closing">{fmt(invKitchenClosing)}</p>
                </div>
              </div>
              {!invConsumption && (
                <p className="text-[11px] text-[#5C6B64] mt-2 text-center italic">Complete closing in Kitchen module</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-[#E2E8DF] shadow-sm overflow-hidden" data-testid="bar-closing-section">
            <CardContent className="px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Wine className="w-4 h-4 text-[#1D4ED8]" strokeWidth={1.75} />
                  <h3 className="text-sm font-semibold text-[#1A2421]">Beverage Cost</h3>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-[#5C6B64] bg-[#F3F5F2] px-2 py-0.5 rounded-full font-medium">From Inventory</span>
              </div>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Consumption</p>
                  <p className={`text-2xl font-bold tabular-nums ${invBarConsumption < 0 ? 'text-[#C45A41]' : 'text-[#1D4ED8]'}`}
                    data-testid="bar-consumption-display">{fmt(invBarConsumption)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Bev Cost %</p>
                  {bevCostPct !== null ? (
                    <p className={`text-lg font-bold tabular-nums ${parseFloat(bevCostPct) > 30 ? 'text-[#C45A41]' : 'text-[#1D4ED8]'}`}
                      data-testid="bev-cost-pct-display">{bevCostPct}%</p>
                  ) : (
                    <p className="text-[11px] text-[#5C6B64]">Enter sales split</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-[#E2E8DF]">
                <div>
                  <p className="text-[9px] text-[#5C6B64] uppercase">Opening</p>
                  <p className="text-xs font-semibold text-[#1A2421] tabular-nums" data-testid="inv-bar-opening">{fmt(invBarOpening)}</p>
                </div>
                <div>
                  <p className="text-[9px] text-[#5C6B64] uppercase">Issues</p>
                  <p className="text-xs font-semibold text-amber-600 tabular-nums" data-testid="inv-bar-issues">{fmt(invBarIssues)}</p>
                </div>
                <div>
                  <p className="text-[9px] text-[#5C6B64] uppercase">Closing</p>
                  <p className="text-xs font-semibold text-[#1A2421] tabular-nums" data-testid="inv-bar-closing">{fmt(invBarClosing)}</p>
                </div>
              </div>
              {!invConsumption && (
                <p className="text-[11px] text-[#5C6B64] mt-2 text-center italic">Complete closing in Bar module</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Store Issues Section */}
        <Card className={`border-[#E2E8DF] shadow-sm overflow-hidden ${otherLocked ? 'border-l-4 border-l-[#2D6A4F]' : ''}`}>
          <SectionHeader icon={Package} title="Store Issues / Transfers" open={sections.other} onToggle={() => toggleSection('other')}
            sectionStatus={sectionSubs.other_issues || { locked: false }}
            pinCorrected={pinUnlocked[`${dateStr}:other_issues`]} />
          {sections.other && (
            <CardContent className="px-4 pt-0 pb-4 section-enter">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <NumberInput label="Kitchen Issues" value={form.kitchen_issues} onChange={v => updateField('kitchen_issues', v)} testId="kitchen-issues-input" disabled={otherLocked} />
                <NumberInput label="Bar Issues" value={form.bar_issues} onChange={v => updateField('bar_issues', v)} testId="bar-issues-input" disabled={otherLocked} />
                <NumberInput label="HK Issues" value={form.hk_issues} onChange={v => updateField('hk_issues', v)} testId="hk-issues-input" disabled={otherLocked} />
                <NumberInput label="Store Transfers" value={form.other_purchases} onChange={v => updateField('other_purchases', v)} testId="other-purchases-input" disabled={otherLocked} />
              </div>
              {editingSection === 'other_issues' ? (
                <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
                  <Button onClick={() => handleEditSave('other_issues')} disabled={!!sectionSubmitting}
                    className="w-full h-10 bg-[#D4A017] hover:bg-[#B8901A] text-white text-sm"
                    data-testid="save-edit-other_issues">
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    {sectionSubmitting === 'other_issues' ? 'Saving...' : 'Save Edit'}
                  </Button>
                </div>
              ) : (
                <SectionSubmitBar sectionKey="other_issues" sectionSub={sectionSubs.other_issues}
                  onSubmit={handleSectionSubmitClick} onEdit={handleSectionEditClick}
                  submitting={sectionSubmitting === 'other_issues'} editMode={editMode}
                  onRequestOverride={handleRequestOverride} overrideStatus={getOverrideStatus('other_issues')}
                  moduleWritable={canEditModule('store_issues')}
                  isPastDate={isPastDate} pinSet={user?.pin_set} onPinEdit={handlePinEdit} />
              )}
            </CardContent>
          )}
        </Card>

        {/* ITER243 — Settlement (POS import, read-only) */}
        {settlement && settlement.length > 0 && (
          <Card className="border-[#E2E8DF] shadow-sm overflow-hidden" data-testid="settlement-card">
            <div className="p-4 border-b border-[#E2E8DF] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[color:var(--color-primary)]" strokeWidth={1.5} />
                <h3 className="font-semibold text-[#1A2421]">Settlement (from WinHMS)</h3>
                <Badge className="bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/20 text-[10px]">
                  Read-only · payment methods (not sales channels)
                </Badge>
              </div>
              <a
                href={`${API}/pos-import/settlement/export.csv?outlet_id=${user?.outlet_id}&start_date=${dateStr}&end_date=${dateStr}`}
                target="_blank" rel="noreferrer"
                className="text-[12px] text-[color:var(--color-primary)] hover:underline font-medium"
                onClick={(e) => {
                  // Force the Authorization header onto the download — easier to
                  // open via fetch + blob; keep simple fallback link for sessions
                  // where the token is still valid in the browser bar (admins
                  // typically open this from the same tab).
                  e.preventDefault();
                  axios.get(`${API}/pos-import/settlement/export.csv`,
                    { params: { outlet_id: user?.outlet_id, start_date: dateStr, end_date: dateStr },
                      headers: getHeaders(), responseType: 'blob' })
                    .then(res => {
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `settlement_${dateStr}.csv`;
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    })
                    .catch(() => toast.error('Failed to download settlement CSV'));
                }}
                data-testid="settlement-download-csv"
              >
                Download CSV
              </a>
            </div>
            <CardContent className="px-4 pt-3 pb-4">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[#5C6B64] border-b border-[#E2E8DF]">
                    <th className="py-1.5 font-medium">Settle Mode</th>
                    <th className="py-1.5 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody data-testid="settlement-rows">
                  {settlement.map((row, i) => (
                    <tr key={i} className="border-b border-[#F3F5F2] last:border-0">
                      <td className="py-1.5 text-[#1A2421]">{row.mode}</td>
                      <td className="py-1.5 text-right tabular-nums font-medium text-[#1A2421]">{fmt(row.amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#E2E8DF]">
                    <td className="py-1.5 text-[#5C6B64] font-medium">Total</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold text-[color:var(--color-primary)]">
                      {fmt(settlement.reduce((s, r) => s + Number(r.amount || 0), 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
              {posImportMeta?.imported_at_utc && (
                <p className="mt-2 text-[10px] text-[#5C6B64]" data-testid="pos-import-source-label">
                  {/* ITER247 — Source-based label only. Never leak the admin/
                      tenant account email that ran the import to managers. */}
                  Imported from WinHMS · {fmtTimeIST(posImportMeta.imported_at_utc)}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer Experience Section */}
        <Card className={`border-[#E2E8DF] shadow-sm overflow-hidden ${cxLocked ? 'border-l-4 border-l-[#2D6A4F]' : ''}`} data-testid="customer-experience-section">
          <SectionHeader icon={Star} title="Customer Experience" open={sections.cx} onToggle={() => toggleSection('cx')}
            sectionStatus={sectionSubs.customer_experience || { locked: false }}
            pinCorrected={pinUnlocked[`${dateStr}:customer_experience`]} />
          {sections.cx && (
            <CardContent className="px-4 pt-0 pb-4 section-enter">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <NumberInput label="Direct Reviews" value={form.direct_reviews} onChange={v => updateField('direct_reviews', v)} testId="direct-reviews-input" placeholder="0" disabled={cxLocked} />
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-medium text-[#1A2421]">Avg Rating (0-5)</Label>
                  <Input data-testid="avg-rating-day-input" type="number" inputMode="decimal" step="0.1" min="0" max="5"
                    placeholder="4.2" value={form.avg_rating_day}
                    onChange={e => updateField('avg_rating_day', e.target.value)}
                    disabled={cxLocked}
                    className={`h-10 bg-white border border-[#1A2421]/15 hover:border-[#1A2421]/30 focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] text-sm placeholder:text-[#5C6B64]/60 ${cxLocked ? 'bg-[#F3F5F2] cursor-not-allowed opacity-70' : ''}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-medium text-[#1A2421]">NPS (1-10)</Label>
                  <Input data-testid="nps-day-input" type="number" inputMode="decimal" step="0.1" min="1" max="10"
                    placeholder="8" value={form.nps_day}
                    onChange={e => updateField('nps_day', e.target.value)}
                    disabled={cxLocked}
                    className={`h-10 bg-white border border-[#1A2421]/15 hover:border-[#1A2421]/30 focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] text-sm placeholder:text-[#5C6B64]/60 ${cxLocked ? 'bg-[#F3F5F2] cursor-not-allowed opacity-70' : ''}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-medium text-[#1A2421]">Avg Rating MTD</Label>
                  <div className="h-10 bg-[color:var(--color-primary)]/[0.04] border border-[color:var(--color-primary)]/15 rounded-md flex items-center px-3" data-testid="avg-rating-mtd-display">
                    <Star className="w-4 h-4 text-[color:var(--color-primary)] mr-2" strokeWidth={1.5} />
                    <span className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums">{mtdRating.avg_rating_mtd || '---'}</span>
                    <span className="text-[10px] text-[#5C6B64] ml-2">({mtdRating.entries_count}d, {mtdRating.total_reviews_mtd} rev)</span>
                  </div>
                </div>
              </div>
              {editingSection === 'customer_experience' ? (
                <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
                  <Button onClick={() => handleEditSave('customer_experience')} disabled={!!sectionSubmitting}
                    className="w-full h-10 bg-[#D4A017] hover:bg-[#B8901A] text-white text-sm"
                    data-testid="save-edit-customer_experience">
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    {sectionSubmitting === 'customer_experience' ? 'Saving...' : 'Save Edit'}
                  </Button>
                </div>
              ) : (
                <SectionSubmitBar sectionKey="customer_experience" sectionSub={sectionSubs.customer_experience}
                  onSubmit={handleSectionSubmitClick} onEdit={handleSectionEditClick}
                  submitting={sectionSubmitting === 'customer_experience'} editMode={editMode}
                  onRequestOverride={handleRequestOverride} overrideStatus={getOverrideStatus('customer_experience')}
                  moduleWritable={canEditModule('daily_entry')}
                  isPastDate={isPastDate} pinSet={user?.pin_set} onPinEdit={handlePinEdit} />
              )}
            </CardContent>
          )}
        </Card>

        {/* Notes Section */}
        <Card className={`border-[#E2E8DF] shadow-sm overflow-hidden ${notesLocked ? 'border-l-4 border-l-[#2D6A4F]' : ''}`}>
          <SectionHeader icon={FileText} title="Notes" open={sections.notes} onToggle={() => toggleSection('notes')}
            sectionStatus={sectionSubs.notes || { locked: false }}
            pinCorrected={pinUnlocked[`${dateStr}:notes`]} />
          {sections.notes && (
            <CardContent className="px-4 pt-0 pb-4 section-enter">
              <Textarea data-testid="remarks-input" placeholder="Any remarks or observations for the day..."
                value={form.remarks} onChange={e => updateField('remarks', e.target.value)} rows={3}
                disabled={notesLocked}
                className={`bg-white border border-[#1A2421]/15 hover:border-[#1A2421]/30 focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] text-sm resize-none placeholder:text-[#5C6B64]/60 ${notesLocked ? 'bg-[#F3F5F2] cursor-not-allowed opacity-70' : ''}`} />
              {editingSection === 'notes' ? (
                <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
                  <Button onClick={() => handleEditSave('notes')} disabled={!!sectionSubmitting}
                    className="w-full h-10 bg-[#D4A017] hover:bg-[#B8901A] text-white text-sm"
                    data-testid="save-edit-notes">
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    {sectionSubmitting === 'notes' ? 'Saving...' : 'Save Edit'}
                  </Button>
                </div>
              ) : (
                <SectionSubmitBar sectionKey="notes" sectionSub={sectionSubs.notes}
                  onSubmit={handleSectionSubmitClick} onEdit={handleSectionEditClick}
                  submitting={sectionSubmitting === 'notes'} editMode={editMode}
                  onRequestOverride={handleRequestOverride} overrideStatus={getOverrideStatus('notes')}
                  moduleWritable={canEditModule('daily_entry')}
                  isPastDate={isPastDate} pinSet={user?.pin_set} onPinEdit={handlePinEdit} />
              )}
            </CardContent>
          )}
        </Card>

        {/* Summary + Global Submit */}
        {canEdit && (
          <div className="sticky-submit">
            {netSales > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-3 text-center">
                <div className="p-2 bg-white rounded-lg border border-[#E2E8DF]">
                  <p className="text-xs text-[#5C6B64]">Food Cost %</p>
                  <p className={`font-semibold ${parseFloat(foodCostPct) > 35 ? 'text-[#C45A41]' : 'text-[color:var(--color-primary)]'}`} data-testid="food-cost-metric">{foodCostPct}%</p>
                </div>
                <div className="p-2 bg-white rounded-lg border border-[#E2E8DF]">
                  <p className="text-xs text-[#5C6B64]">Bev Cost %</p>
                  <p className={`font-semibold ${parseFloat(bevCostPct) > 30 ? 'text-[#C45A41]' : 'text-[color:var(--color-primary)]'}`} data-testid="beverage-cost-metric">{bevCostPct}%</p>
                </div>
                <div className="p-2 bg-white rounded-lg border border-[#E2E8DF]">
                  <p className="text-xs text-[#5C6B64]">Total APC</p>
                  <p className="font-semibold text-[color:var(--color-primary)]" data-testid="total-apc-metric">{covers > 0 ? fmt(parseFloat(totalApc)) : '---'}</p>
                </div>
              </div>
            )}
            {/* ITER258 — The form-level "Submit/Update Report" button was
                removed. Each section is committed via its own
                Submit/Save Edit button inside the section card; those
                go through /submit-section + /edit-section which respect
                the lock/PIN state machine and recompute `calculated`
                from the merged entry. The legacy entry-wide button
                called PUT /daily-entries/{id} which bypassed the state
                machine and zeroed room_revenue (ITER257 root-cause).
                Per-section commits remain the canonical path. */}
          </div>
        )}

      </form>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(o) => !o && setConfirmDialog({ open: false, type: '', sectionKey: '' })}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1A2421]">
              {confirmDialog.type === 'submit' ? 'Submit Section?' : 'Edit Submitted Data?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B64]">
              {confirmDialog.type === 'submit'
                ? `Are you sure you want to submit ${SECTION_LABELS[confirmDialog.sectionKey] || ''}? Once submitted, this section will be locked for the day.`
                : `You are editing submitted data. You can only edit this section ${2 - (confirmDialog.editCount || 0)} more time(s) today. Continue?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E8DF] text-[#5C6B64]" data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}
              className={confirmDialog.type === 'submit' ? 'bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white' : 'bg-[#D4A017] hover:bg-[#B8901A] text-white'}
              data-testid="confirm-action">
              {confirmDialog.type === 'submit' ? 'Submit' : 'Continue Editing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Override Request Dialog */}
      <AlertDialog open={overrideDialog.open} onOpenChange={open => !open && setOverrideDialog({ open: false, sectionKey: '' })}>
        <AlertDialogContent className="border-[#E2E8DF]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1A2421] flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" /> Request Override
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B64] text-sm">
              Request admin to unlock <strong>{SECTION_LABELS[overrideDialog.sectionKey]}</strong> for {dateStr}.
              Your reason will be sent to the admin for review.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs text-[#5C6B64]">Reason (mandatory)</Label>
            <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
              placeholder="Explain why this section needs to be re-opened..."
              className="w-full mt-1 p-2 border border-[#E2E8DF] rounded-md text-sm min-h-[80px] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              data-testid="override-reason-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E8DF] text-[#5C6B64]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitOverrideRequest}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-override-request">
              Submit Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PIN Verification Dialog (for override-approved past-date edits) */}
      <AlertDialog open={pinDialog.open} onOpenChange={open => { if (!open) setPinDialog({ open: false, sectionKey: '', action: '' }); }}>
        <AlertDialogContent className="border-[#E2E8DF]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1A2421] flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-emerald-600" /> Enter PIN to Edit
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B64] text-sm">
              Override approved for <strong>{SECTION_LABELS[pinDialog.sectionKey]}</strong> on {dateStr}.
              Enter your 4-digit PIN to proceed with editing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs text-[#5C6B64]">4-digit PIN</Label>
            <Input
              data-testid="pin-input"
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder="----"
              value={pinValue}
              onChange={e => { setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
              className="mt-1 h-12 text-center text-2xl tracking-[0.5em] font-mono border-[#E2E8DF] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
            {pinError && <p className="text-xs text-red-600 mt-1" data-testid="pin-error">{pinError}</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E8DF] text-[#5C6B64]" data-testid="pin-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePinConfirm}
              disabled={pinValue.length !== 4}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pin-confirm">
              Verify & Edit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ITER243 — WinHMS POS Import modal */}
      <PosImportModal
        open={posImportOpen}
        onClose={() => setPosImportOpen(false)}
        outletId={user?.outlet_id}
        onApplied={applyPosImport}
      />
    </div>
    </div>
  );
}
